/**
 * AdminConcierge — mint a personalised welcome audio for any leader
 * in ~2 minutes. Lives at `/admin/concierge` (admin-only).
 *
 * Flow:
 *   1. Search existing members OR create a new one inline (name + email)
 *   2. Type 3-5 context bullets (relocation · big role · life event etc.)
 *   3. Pick which Reclaim areas to introduce
 *   4. Hit "Draft script" → Claude generates the 12-paragraph script
 *      in Michael's voice (~3 sec)
 *   5. Inline-edit any paragraph · live cost + duration estimate
 *   6. "Render & deliver" → ElevenLabs + ffmpeg → upserts welcome_audios
 *      → next time the member logs in, the warm concierge card is on
 *      their home screen with their name on it
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Check,
  AlertTriangle,
  X,
  Search,
  UserPlus,
  Wand2,
  RotateCw,
  Plus,
  Trash2,
  Link as LinkIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

/**
 * MagicLinkButton — mints an admin one-tap sign-in URL for any user
 * (via `POST /api/admin/magic-link/{user_id}`) and copies it straight
 * to the clipboard. No email dependency. Any admin can paste the URL
 * into WhatsApp / iMessage / SMS and the recipient is signed in on
 * a single tap.
 */
function MagicLinkButton({ userId, ...rest }) {
  const [state, setState] = useState("idle"); // idle | minting | copied | error
  const [url, setUrl] = useState(null);

  const handleClick = async (e) => {
    e.stopPropagation();
    setState("minting");
    try {
      const r = await fetch(`${API}/api/admin/magic-link/${userId}`, {
        method: "POST",
        headers: auth(),
      });
      if (!r.ok) throw new Error(`mint_${r.status}`);
      const data = await r.json();
      const magicUrl = data.magic_url;
      setUrl(magicUrl);
      try {
        await navigator.clipboard.writeText(magicUrl);
        setState("copied");
      } catch {
        // Clipboard blocked — surface the URL inline for manual copy
        setState("copied");
      }
      setTimeout(() => setState("idle"), 4500);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {state === "copied" && url && (
        <span
          className="text-[10px] text-emerald-700 max-w-[200px] truncate hidden sm:inline"
          title={url}
        >
          Copied · paste it anywhere
        </span>
      )}
      <button
        onClick={handleClick}
        disabled={state === "minting"}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
          state === "copied"
            ? "bg-emerald-50 border border-emerald-300 text-emerald-800"
            : state === "error"
              ? "bg-red-50 border border-red-300 text-red-800"
              : "bg-white border border-[#D6D3D1] text-[#292524] hover:border-amber-400 hover:bg-amber-50"
        }`}
        title="Mint a one-tap sign-in URL for this user and copy it"
        {...rest}
      >
        {state === "minting" && <Loader2 size={12} className="animate-spin" />}
        {state === "copied" && <Check size={12} />}
        {state === "error" && <AlertTriangle size={12} />}
        {state === "idle" && <LinkIcon size={12} />}
        {state === "idle" && "Copy magic link"}
        {state === "minting" && "Minting…"}
        {state === "copied" && "Copied!"}
        {state === "error" && "Try again"}
      </button>
    </div>
  );
}

const DEFAULT_AREAS = ["Atelier", "Inner Path", "Golf", "Day & Night"];

export default function AdminConcierge() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Step 1 · target user ────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [target, setTarget] = useState(null); // {id, name, email, …}
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPasswordOut, setNewPasswordOut] = useState(null);

  // ── Step 2 · context ────────────────────────────────────────────
  const [bullets, setBullets] = useState(["", "", ""]);
  const [areas, setAreas] = useState([...DEFAULT_AREAS]);

  // ── Step 3 · drafted script ─────────────────────────────────────
  const [drafting, setDrafting] = useState(false);
  const [paragraphs, setParagraphs] = useState(null);
  const [draftMeta, setDraftMeta] = useState(null); // {char_count, estimated_duration_s, estimated_cost_usd}

  // ── Step 4 · render ─────────────────────────────────────────────
  const [renderJob, setRenderJob] = useState(null);
  const pollTimer = useRef(null);

  const [flash, setFlash] = useState(null);

  // ── User search (debounced) ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (search.trim().length < 1) { setMatches([]); return; }
      try {
        const r = await fetch(`${API}/api/admin/concierge/users?search=${encodeURIComponent(search)}`,
          { headers: auth(), cache: "no-store" });
        if (r.ok && !cancelled) setMatches((await r.json()).items || []);
      } catch (_) { /* silent */ }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  // ── Live cost / duration estimate as admin edits ────────────────
  const liveStats = paragraphs ? (() => {
    const chars = paragraphs.reduce((s, p) => s + (p?.length || 0), 0);
    return {
      chars,
      duration_s: Math.round(chars / 14),
      cost_usd: (chars * 0.00022).toFixed(2),
    };
  })() : null;

  // ── Handlers ────────────────────────────────────────────────────
  const handleCreateUser = async () => {
    if (!newName.trim() || !newEmail.trim()) return;
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/admin/concierge/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ name: newName.trim(), email: newEmail.trim() }),
      });
      const j = await r.json();
      if (!r.ok) {
        setFlash({ kind: "err", text: j.detail || "Create failed" });
      } else {
        setTarget(j.user);
        setNewName(""); setNewEmail("");
        if (j.temp_password) setNewPasswordOut(j.temp_password);
        setFlash({ kind: "ok", text: j.created ? "User created" : "User already existed — selected" });
      }
    } catch (_) {
      setFlash({ kind: "err", text: "Create failed (network)" });
    }
    setCreating(false);
  };

  const handleDraft = async () => {
    if (!target) return;
    const cleanBullets = bullets.map((b) => b.trim()).filter(Boolean);
    if (cleanBullets.length === 0) {
      setFlash({ kind: "err", text: "Add at least one context bullet" });
      return;
    }
    setDrafting(true); setParagraphs(null); setDraftMeta(null); setFlash(null);
    try {
      const r = await fetch(`${API}/api/admin/concierge/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          name: target.name,
          context_bullets: cleanBullets,
          areas: areas.filter(Boolean),
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        setFlash({ kind: "err", text: j.detail || "Draft failed" });
      } else {
        setParagraphs(j.paragraphs);
        setDraftMeta({
          char_count: j.char_count,
          duration_s: j.estimated_duration_s,
          cost_usd: j.estimated_cost_usd,
        });
      }
    } catch (_) {
      setFlash({ kind: "err", text: "Draft failed (network)" });
    }
    setDrafting(false);
  };

  const handleRender = async () => {
    if (!target || !paragraphs?.length) return;
    setFlash(null);
    setRenderJob({ status: "queued", started_at: new Date().toISOString() });
    // Kick off the queue POST with a small retry — mobile networks flake
    // for a beat and drop the first request. We attempt up to 3 times
    // (0, 800ms, 1600ms backoff) before surfacing the network error so
    // the admin doesn't lose their carefully-drafted script.
    const postWithRetry = async () => {
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) {
          await new Promise((res) => setTimeout(res, attempt * 800));
        }
        try {
          const r = await fetch(`${API}/api/admin/concierge/render`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...auth() },
            body: JSON.stringify({
              user_id: target.id,
              paragraphs,
              title: `Welcome, ${target.name}`,
            }),
          });
          return r;
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr || new Error("network");
    };
    try {
      const r = await postWithRetry();
      const j = await r.json();
      if (!r.ok) {
        setRenderJob(null);
        setFlash({ kind: "err", text: j.detail || "Render failed to start", retryable: true });
        return;
      }
      setRenderJob(j);
      // Poll
      pollTimer.current = setInterval(async () => {
        try {
          const r2 = await fetch(`${API}/api/admin/concierge/status/${j.job_id}`, {
            headers: auth(), cache: "no-store",
          });
          if (r2.ok) {
            const status = await r2.json();
            setRenderJob(status);
            if (status.status === "done" || status.status === "failed") {
              clearInterval(pollTimer.current);
              setFlash({
                kind: status.status === "done" ? "ok" : "err",
                text: status.status === "done"
                  ? `Render complete · ${status.result?.duration_s}s · ${status.result?.size_mb}MB · live on their home`
                  : `Render failed: ${status.error}`,
                retryable: status.status === "failed",
              });
            }
          }
        } catch (_) { /* keep polling */ }
      }, 2000);
    } catch (_) {
      setRenderJob(null);
      setFlash({
        kind: "err",
        text: "Render failed (network). Your script is safe — tap Retry.",
        retryable: true,
      });
    }
  };

  // Clean up the poll on unmount.
  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current); }, []);

  const reset = () => {
    setTarget(null); setSearch(""); setMatches([]); setNewPasswordOut(null);
    setBullets(["", "", ""]); setAreas([...DEFAULT_AREAS]);
    setParagraphs(null); setDraftMeta(null); setRenderJob(null); setFlash(null);
  };

  // ── Access gate ────────────────────────────────────────────────
  if (user?.role !== "admin") {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
          <p className="text-[#78716C]">Admin access required.</p>
        </div>
      </AdminLayout>
    );
  }

  const renderRunning = renderJob && (renderJob.status === "queued" || renderJob.status === "running");

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-concierge">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-[#292524] flex items-center gap-2">
              <Sparkles size={18} className="text-amber-500" />
              Concierge Welcome Studio
            </h2>
            <p className="text-sm text-[#78716C]">
              Mint a personal welcome audio for any leader in ~2 minutes.
              Search a member or create one on the fly, add context, draft with AI, render with V3.
            </p>
          </div>
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#292524]" data-testid="concierge-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
        </div>

        {flash && (
          <div
            className={`rounded-xl px-4 py-3 text-sm flex items-center justify-between ${
              flash.kind === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                                  : "bg-red-50 text-red-800 border border-red-200"
            }`}
            data-testid="concierge-flash"
          >
            <div className="flex items-center gap-2">
              {flash.kind === "ok" ? <Check size={14} /> : <AlertTriangle size={14} />}
              <span>{flash.text}</span>
            </div>
            <div className="flex items-center gap-2">
              {flash.retryable && paragraphs?.length > 0 && target && (
                <button
                  onClick={handleRender}
                  className="text-xs font-medium underline hover:no-underline"
                  data-testid="concierge-retry-render"
                >
                  Retry
                </button>
              )}
              <button onClick={() => setFlash(null)}><X size={14} /></button>
            </div>
          </div>
        )}

        {/* ── Step 1 · select / create user ───────────────────── */}
        <Section step={1} title="Who is this welcome for?" done={!!target}>
          {target ? (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="concierge-target-selected">
              <div>
                <div className="font-medium text-[#292524]">{target.name}</div>
                <div className="text-xs text-[#78716C]">{target.email} · {target.membership_tier || "premium"}</div>
              </div>
              <div className="flex items-center gap-2">
                <MagicLinkButton userId={target.id} data-testid="concierge-target-magic-link" />
                <button onClick={reset} className="text-xs text-[#78716C] hover:text-[#292524]" data-testid="concierge-target-change">
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* search existing */}
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search existing members by name or email"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#D6D3D1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="concierge-user-search"
                />
              </div>
              {matches.length > 0 && (
                <div className="rounded-xl border border-[#E6E2DD] overflow-hidden" data-testid="concierge-user-matches">
                  {matches.map((m) => (
                    <div
                      key={m.id}
                      className="w-full px-4 py-2.5 hover:bg-[#F5F5F4] flex items-center justify-between text-sm border-b border-[#E6E2DD] last:border-b-0"
                    >
                      <button
                        onClick={() => setTarget(m)}
                        className="flex-1 text-left flex items-center gap-2"
                        data-testid={`concierge-user-${m.id}`}
                      >
                        <div>
                          <div className="font-medium text-[#292524]">{m.name}</div>
                          <div className="text-xs text-[#78716C]">{m.email}</div>
                        </div>
                        {m.has_welcome && (
                          <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5 ml-2">
                            welcomed
                          </span>
                        )}
                      </button>
                      <MagicLinkButton userId={m.id} data-testid={`concierge-user-${m.id}-magic-link`} />
                    </div>
                  ))}
                </div>
              )}

              {/* divider */}
              <div className="flex items-center gap-3 my-2 text-[10px] uppercase tracking-wider text-[#A8A29E]">
                <div className="h-px flex-1 bg-[#E6E2DD]" />
                <span>or create new</span>
                <div className="h-px flex-1 bg-[#E6E2DD]" />
              </div>

              {/* create new */}
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Full name"
                  className="px-3 py-2.5 rounded-xl border border-[#D6D3D1] bg-white text-sm"
                  data-testid="concierge-new-name"
                />
                <input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Email"
                  type="email"
                  className="px-3 py-2.5 rounded-xl border border-[#D6D3D1] bg-white text-sm"
                  data-testid="concierge-new-email"
                />
              </div>
              <button
                onClick={handleCreateUser}
                disabled={!newName.trim() || !newEmail.trim() || creating}
                className="rounded-full bg-[#292524] hover:bg-black text-white text-xs px-4 py-2 flex items-center gap-1.5 disabled:opacity-40"
                data-testid="concierge-create-user-btn"
              >
                {creating ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                Create user
              </button>

              {newPasswordOut && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs" data-testid="concierge-new-password-banner">
                  <div className="font-medium text-amber-900 mb-1">Save this temp password — won&apos;t show again:</div>
                  <code className="text-amber-900 font-mono">{newPasswordOut}</code>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── Step 2 · context ─────────────────────────────────── */}
        <Section step={2} title="Tell me about them" disabled={!target}>
          <div className="space-y-2">
            <p className="text-xs text-[#78716C]">3-5 specific bullets. Names, locations, life events, what they care about. Be specific — Claude uses these directly.</p>
            {bullets.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-[#A8A29E] w-5">{i + 1}</span>
                <input
                  value={b}
                  onChange={(e) => setBullets(bullets.map((x, idx) => idx === i ? e.target.value : x))}
                  placeholder={i === 0 ? "Just moved Orlando → Uttoxeter UK"
                              : i === 1 ? "Sister of Sam — supporting his golf transformation"
                              : i === 2 ? "Feeling stress & overwhelm from relocation"
                              : "additional context (optional)"}
                  className="flex-1 px-3 py-2 rounded-lg border border-[#D6D3D1] bg-white text-sm"
                  data-testid={`concierge-bullet-${i}`}
                />
                {bullets.length > 1 && (
                  <button onClick={() => setBullets(bullets.filter((_, idx) => idx !== i))} className="text-[#A8A29E] hover:text-red-500" data-testid={`concierge-bullet-remove-${i}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => setBullets([...bullets, ""])} className="text-xs flex items-center gap-1 text-amber-700 hover:text-amber-800" data-testid="concierge-add-bullet">
              <Plus size={12} /> Add bullet
            </button>
          </div>

          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wider text-[#78716C] mb-2">Reclaim areas to introduce</p>
            <div className="flex flex-wrap gap-1.5">
              {["Atelier", "Inner Path", "Golf", "Day & Night", "Reclaim Presence", "Sleep Biohacking", "Daily Awareness"].map((a) => {
                const on = areas.includes(a);
                return (
                  <button
                    key={a}
                    onClick={() => setAreas(on ? areas.filter((x) => x !== a) : [...areas, a])}
                    className={`px-2.5 py-1 rounded-full text-xs ${on ? "bg-amber-500 text-white" : "bg-[#F5F5F4] text-[#78716C] hover:bg-[#E6E2DD]"}`}
                    data-testid={`concierge-area-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={handleDraft}
            disabled={!target || drafting || bullets.every((b) => !b.trim())}
            className="mt-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-40"
            data-testid="concierge-draft-btn"
          >
            {drafting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            Draft script with AI
          </button>
        </Section>

        {/* ── Step 3 · review + edit ───────────────────────────── */}
        {paragraphs && (
          <Section step={3} title="Review & tweak the script" done={paragraphs.length > 0}>
            <div className="rounded-xl bg-[#FAFAF9] border border-[#E6E2DD] px-4 py-3 text-xs text-[#78716C] flex items-center justify-between" data-testid="concierge-script-stats">
              <span>
                {paragraphs.length} paragraphs · {liveStats?.chars || 0} chars ·
                {" "}~{Math.floor((liveStats?.duration_s || 0) / 60)}:{String((liveStats?.duration_s || 0) % 60).padStart(2, "0")} spoken
              </span>
              <span className="font-medium text-amber-700">
                est cost ~${liveStats?.cost_usd}
              </span>
            </div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1 mt-3">
              {paragraphs.map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs text-[#A8A29E] w-5 pt-2 shrink-0">{i + 1}</span>
                  <textarea
                    value={p}
                    onChange={(e) => setParagraphs(paragraphs.map((x, idx) => idx === i ? e.target.value : x))}
                    rows={Math.max(2, Math.ceil(p.length / 80))}
                    className="flex-1 px-3 py-2 rounded-lg border border-[#D6D3D1] bg-white text-sm leading-relaxed resize-y"
                    data-testid={`concierge-para-${i}`}
                  />
                  <button onClick={() => setParagraphs(paragraphs.filter((_, idx) => idx !== i))} className="text-[#A8A29E] hover:text-red-500 mt-2" data-testid={`concierge-para-remove-${i}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <button onClick={handleDraft} disabled={drafting} className="rounded-full bg-[#F5F5F4] hover:bg-[#E6E2DD] text-[#292524] text-xs px-3 py-2 flex items-center gap-1.5" data-testid="concierge-redraft-btn">
                <RotateCw size={12} /> Re-draft with AI
              </button>
              <button
                onClick={handleRender}
                disabled={renderRunning}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-40"
                data-testid="concierge-render-btn"
              >
                {renderRunning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Render & deliver
              </button>
            </div>
          </Section>
        )}

        {/* ── Step 4 · render status ───────────────────────────── */}
        {renderJob && (
          <Section step={4} title="Render progress" done={renderJob.status === "done"}>
            <div className="rounded-xl border border-[#E6E2DD] bg-white px-4 py-3 space-y-2" data-testid="concierge-render-status">
              <div className="flex items-center gap-2 text-sm">
                {renderJob.status === "done"   && <Check size={14} className="text-emerald-600" />}
                {renderJob.status === "failed" && <AlertTriangle size={14} className="text-red-600" />}
                {(renderJob.status === "queued" || renderJob.status === "running") && <Loader2 size={14} className="animate-spin text-amber-600" />}
                <span className="font-medium capitalize">{renderJob.status}</span>
                {renderJob.result && (
                  <span className="text-xs text-[#78716C]">
                    · {renderJob.result.duration_s}s · {renderJob.result.size_mb}MB
                  </span>
                )}
              </div>
              {renderJob.error && (
                <div className="text-xs text-red-700">{renderJob.error}</div>
              )}
              {renderJob.status === "done" && target && (
                <div className="text-xs text-emerald-700">
                  ✓ Live on {target.name}&apos;s home screen. Next time they log in, the warm amber card plays automatically.
                </div>
              )}
            </div>
            {renderJob.status === "done" && (
              <button onClick={reset} className="mt-3 text-xs text-amber-700 hover:text-amber-800 flex items-center gap-1" data-testid="concierge-mint-another">
                <Sparkles size={12} /> Mint another welcome
              </button>
            )}
          </Section>
        )}
      </div>
    </AdminLayout>
  );
}

function Section({ step, title, children, done, disabled }) {
  return (
    <div className={`rounded-2xl border ${done ? "border-emerald-300 bg-emerald-50/30" : "border-[#E6E2DD] bg-white"} px-5 py-4 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
          done ? "bg-emerald-500 text-white" : "bg-[#F5F5F4] text-[#78716C]"
        }`}>
          {done ? <Check size={14} /> : step}
        </div>
        <h3 className="font-medium text-[#292524]">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}
